import type { AxiosInstance } from '@glovo/fetcher'
import { LRUCache } from 'lru-cache'
import { cache as reactCache } from 'react'
import { EXPIRATION_TIME_IN_MILLIS } from './constants'
import type {
  CitiesContentResponse,
  City,
  Country,
  CountryContentResponse,
  InvoiceCountries,
} from './types'
import { getConfig } from '@/tools/config'
import { createGlovoError } from '@/tools/error-handling'
import { fetcher as clientFetcher } from '@/tools/fetcher/client'
import type { Language } from '@/tools/i18n'

const cache = new LRUCache({
  max: 1000,
  ttl: EXPIRATION_TIME_IN_MILLIS,
})

interface AllCitiesAndCountries {
  cities: City[]
  countries: Country[]
}
export const getAllCitiesAndCountries = async (
  language: Language,
  fetcher: AxiosInstance = clientFetcher
): Promise<AllCitiesAndCountries> => {
  const { apiUrl } = getConfig()
  const cacheKey = `countries-in-language-${language}`
  const cachedResponse = cache.has(cacheKey) && cache.get(cacheKey)
  if (cachedResponse) {
    return cachedResponse as unknown as AllCitiesAndCountries
  }

  const response = await fetcher.get(`${apiUrl}/v3/cities/summarized_settings?language=${language}`)
  const allCities: City[] = Object.keys(response.data.cities).map((key) => ({
    code: key,
    name: response.data.cities[key].displayName || null,
  }))

  const allCountries: Country[] = Object.keys(response.data.countries).map((key) => ({
    code: key,
    name: response.data.countries[key].name || null,
  }))

  const data = { cities: allCities, countries: allCountries }
  cache.set(cacheKey, data)
  return data
}

export const getInvoiceCountry = async (
  fetcher: AxiosInstance = clientFetcher
): Promise<InvoiceCountries> => {
  const { apiUrl } = getConfig()
  const response = await fetcher.get(`${apiUrl}/v3/countries/invoice_settings`)
  return response.data
}

export const getCountryContent = async (
  { country, language }: { country: string; language: Language },
  fetcher: AxiosInstance = clientFetcher
) => {
  const cacheKey = `country-content-${country}-in-${language}`
  const cachedResponse = cache.has(cacheKey) && cache.get(cacheKey)
  if (cachedResponse) {
    return cachedResponse as unknown as CountryContentResponse
  }
  const { apiUrl } = getConfig()
  const response = await fetcher.get<CountryContentResponse>(
    `${apiUrl}/seo-content/locales/${language}/countries/${country}`
  )

  const data = response.data
  cache.set(cacheKey, data)
  return data
}

export const getCitiesContent = reactCache(
  async (
    { country, language }: { country: string; language: Language },
    fetcher: AxiosInstance = clientFetcher
  ) => {
    const { apiUrl } = getConfig()
    const response = await fetcher.get<CitiesContentResponse>(
      `${apiUrl}/seo-content/locales/${language}/countries/${country}/cities`
    )
    return response.data
  }
)

export const getCountryCodeFromCityCode = reactCache(
  async (cityCode: string, fetcher: AxiosInstance = clientFetcher): Promise<string> => {
    const { apiUrl } = getConfig()
    const response = await fetcher.get(`${apiUrl}/v3/customers/cities?city_code=${cityCode}`)
    if (!response.data?.country?.code) {
      const error = createGlovoError('[getCountryCodeFromCityCode] City not found', { cityCode })
      throw error
    }
    return response.data.country.code
  }
)

export const getAllCountryCodes = reactCache(
  async (fetcher: AxiosInstance = clientFetcher): Promise<string[]> => {
    const citiesAndCountries = await getAllCitiesAndCountries('en', fetcher)
    return citiesAndCountries.countries.map((country) => country.code)
  }
)

export const isSupportedCountryCode = async (
  countryCode: unknown,
  fetcher: AxiosInstance = clientFetcher
): Promise<boolean> => {
  if (!countryCode || typeof countryCode !== 'string') {
    return false
  }
  const normalizedCountryCode = countryCode.toUpperCase()
  const supportedCountryCodes = await getAllCountryCodes(fetcher)
  return Boolean(supportedCountryCodes.includes(normalizedCountryCode))
}
